import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone, TableWrap } from '../components/ui.tsx';
import { locale, t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day as dayText, money, parseMoney } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Kpi, Table } from './Reports.tsx';

/**
 * Product changes (PLM): every change to a recipe or bill of materials goes through a proposal, an
 * approval and a day to apply it. The board shows the changes by where they stand; a change opens
 * with what it changes, line by line, what a batch costs before and after, who approved it and its
 * history. Each applied change gives the bill of materials a new version that stays on record.
 */

type Stage = 'draft' | 'review' | 'approved' | 'done' | 'cancelled';
type Tab = 'board' | 'boms' | 'list' | 'settings';

interface Line {
  item: string;
  qty: number;
  scrap_pct: number;
}
interface Step {
  name: string;
  workcenter: string | null;
  minutes: number;
}
interface Content {
  output_qty: number;
  overhead: number;
  note: string | null;
  lines: Line[];
  steps: Step[];
}
interface Diff {
  header: { field: 'output_qty' | 'overhead' | 'note'; from: number | string | null; to: number | string | null }[];
  lines: { item: string; kind: 'added' | 'removed' | 'changed'; from: { qty: number; scrap_pct: number } | null; to: { qty: number; scrap_pct: number } | null }[];
  steps: { name: string; kind: 'added' | 'removed' | 'changed'; from: { workcenter: string | null; minutes: number } | null; to: { workcenter: string | null; minutes: number } | null }[];
  order: boolean;
  count: number;
}
interface Cost {
  materials: number;
  labour: number;
  overhead: number;
  total: number;
  per_unit: number;
}
interface Names {
  items: Record<string, { name: string; unit: string; unit_cost: number }>;
  workcenters: Record<string, string>;
}
interface Row {
  id: string;
  number: number;
  name: string;
  title: string;
  bom: string;
  bom_name: string;
  item_name: string;
  stage: Stage;
  priority: 'normal' | 'high';
  responsible: string | null;
  responsible_name: string | null;
  created_by_name: string | null;
  effective_on: string | null;
  base_version: number;
  round: number;
  approvals: number;
  applied_version: number | null;
  applied_at: string | null;
  created_at: string;
  updated_at: string;
  behind: boolean;
}
interface Settings {
  approvals_needed: number;
  self_approval: boolean;
}
interface Change extends Row {
  reason: string;
  base: Content;
  proposed: Content;
  current: Content | null;
  diff: Diff;
  conflict: boolean;
  conflict_diff: Diff | null;
  cost: { base: Cost; proposed: Cost };
  names: Names;
  approvals: number;
  events: { kind: string; text: string | null; at: string; by_name: string | null }[];
  planned_orders: number;
  cancel_reason: string | null;
  settings: Settings;
  today: string;
  due: boolean;
  can: { edit: boolean; submit: boolean; decide: boolean; own_review: boolean; decided: 'approved' | 'rejected' | null; apply: boolean; rebase: boolean; cancel: boolean };
}
/** plm.get returns the approvals as a list; the board row has only the count. */
type Detail = Omit<Change, 'approvals'> & { approvals: { round: number; user: string; user_name: string; decision: 'approved' | 'rejected'; note: string | null; at: string; current: boolean }[] };
interface Overview {
  today: string;
  kpi: { drafts: number; review: number; to_apply: number; scheduled: number; done_30: number; waiting_me: number; boms: number };
  settings: Settings;
  can: { propose: boolean; approve: boolean; apply: boolean; configure: boolean };
}
interface Setup {
  boms: { id: string; name: string; item: string; item_name: string; unit: string; version: number }[];
  items: { id: string; name: string; unit: string; unit_cost: number }[];
  workcenters: { id: string; name: string; cost_per_hour: number }[];
  people: { id: string; name: string }[];
  approvers: { id: string; name: string }[];
  settings: Settings;
}
interface BomRow {
  id: string;
  name: string;
  active: boolean;
  item_name: string;
  unit: string;
  version: number;
  changed_at: string | null;
  open: number;
  applied: number;
}
interface History {
  bom: { id: string; name: string; item_name: string; active: boolean };
  versions: {
    version: number;
    applied_at: string;
    applied_by_name: string | null;
    change: { id: string; name: string; title: string } | null;
    note: string | null;
    content: Content;
    diff: Diff | null;
    cost: Cost;
  }[];
  drift: boolean;
  names: Names;
}

const STAGE: Record<Stage, { tone: Tone; icon: IconName }> = {
  draft: { tone: 'neutral', icon: 'pen' },
  review: { tone: 'warn', icon: 'eye' },
  approved: { tone: 'primary', icon: 'checkCircle' },
  done: { tone: 'ok', icon: 'check' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const COLUMNS: Stage[] = ['draft', 'review', 'approved', 'done'];
const OPEN: Stage[] = ['draft', 'review', 'approved'];
/** Words written by the server for a version that was edited outside a change. */
const OUTSIDE = 'Changed outside a change';
const num = (n: number) => new Intl.NumberFormat(locale(), { maximumFractionDigits: 3 }).format(n);
const initials = (n: string | null) =>
  n
    ? n
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join('')
    : '—';
const versionNote = (n: string | null) => (n === OUTSIDE ? t('plm.outside_change') : n);

function StageBadge({ c }: { c: Pick<Row, 'stage'> }) {
  return <StatusBadge tone={STAGE[c.stage].tone} icon={STAGE[c.stage].icon} label={t(`plm.st_${c.stage}` as MessageKey)} />;
}

export function Plm() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const { data: o, reload } = useAction<Overview>('plm.overview');
  const { data: setup, reload: reloadSetup } = useAction<Setup>('plm.setup');
  const tabs: { key: Tab; icon: IconName; show: boolean }[] = [
    { key: 'board', icon: 'layers', show: true },
    { key: 'boms', icon: 'book', show: true },
    { key: 'list', icon: 'list', show: true },
    { key: 'settings', icon: 'settings', show: can('plm.configure') },
  ];
  const visible = tabs.filter((x) => x.show);
  const tab = (visible.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'board') as Tab;
  const open = params.get('c');
  const bomOpen = params.get('bom');
  const [create, setCreate] = useState<{ bom: string | null; restore: number | null } | null>(null);
  const [editNow, setEditNow] = useState(false);
  const [version, setVersion] = useState(0);
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) v === null ? p.delete(k) : p.set(k, v);
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await Promise.all([reload(), reloadSetup()]);
  };
  return (
    <Shell>
      <div className="page reports plm-page">
        <div className="page-head">
          <div>
            <h1>{t('plm.title')}</h1>
            <p className="muted">{t('plm.subtitle')}</p>
          </div>
          {can('plm.propose') ? (
            <div className="page-actions">
              <Button variant="primary" icon="plus" onClick={() => setCreate({ bom: null, restore: null })}>
                {t('plm.new')}
              </Button>
            </div>
          ) : null}
        </div>
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('plm.at_a_glance')}>
            <Kpi label={t('plm.k_drafts')} value={String(o.kpi.drafts)} hint={t('plm.k_drafts_hint')} />
            <Kpi label={t('plm.k_review')} value={String(o.kpi.review)} hint={o.can.approve ? tn('plm.k_waiting_me', o.kpi.waiting_me) : t('plm.k_review_hint')} warn={o.kpi.waiting_me > 0} />
            <Kpi label={t('plm.k_to_apply')} value={String(o.kpi.to_apply)} hint={t('plm.k_to_apply_hint')} warn={o.kpi.to_apply > 0} />
            <Kpi label={t('plm.k_scheduled')} value={String(o.kpi.scheduled)} hint={t('plm.k_scheduled_hint')} />
            <Kpi label={t('plm.k_done')} value={String(o.kpi.done_30)} hint={t('plm.k_done_hint')} />
            <Kpi label={t('plm.k_boms')} value={String(o.kpi.boms)} hint={tn('plm.k_approvals', o.settings.approvals_needed)} />
          </section>
        )}
        <div className="segmented tabs" role="tablist" aria-label={t('plm.sections')}>
          {visible.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'board' ? null : x.key })}>
              <Icon name={x.icon} size={16} />
              {t(`plm.tab_${x.key}` as MessageKey)}
            </button>
          ))}
        </div>
        {!o ? null : tab === 'board' ? (
          <BoardTab key={version} today={o.today} settings={o.settings} onOpen={(id) => set({ c: id })} onNew={o.can.propose ? () => setCreate({ bom: null, restore: null }) : undefined} />
        ) : tab === 'boms' ? (
          <BomsTab key={version} onOpen={(id) => set({ bom: id })} />
        ) : tab === 'list' ? (
          <ListTab key={version} onOpen={(id) => set({ c: id })} />
        ) : (
          <SettingsTab approvers={setup?.approvers ?? []} onChange={changed} />
        )}
      </div>
      {open && setup ? (
        <ChangeDialog
          key={`${open}-${version}`}
          id={open}
          setup={setup}
          editFirst={editNow}
          onClose={() => (setEditNow(false), set({ c: null }))}
          onChange={changed}
          onBom={(id) => set({ c: null, bom: id })}
        />
      ) : null}
      {bomOpen && !open ? (
        <HistoryDialog
          key={`${bomOpen}-${version}`}
          id={bomOpen}
          canPropose={can('plm.propose')}
          onClose={() => set({ bom: null })}
          onOpen={(id) => set({ bom: null, c: id })}
          onPropose={(restore) => setCreate({ bom: bomOpen, restore })}
        />
      ) : null}
      {create && setup ? (
        <NewChange
          setup={setup}
          bom={create.bom}
          restore={create.restore}
          onClose={() => setCreate(null)}
          onSaved={async (id) => {
            setCreate(null);
            await changed();
            // A new change opens on its editor: what it changes is the next thing to write.
            setEditNow(create.restore === null);
            set({ bom: null, c: id });
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- board, bills of materials and list ----------------------------------------------------------

function ChangeCard({ c, settings, onOpen }: { c: Row; settings: Settings; onOpen: () => void }) {
  return (
    <article className={`tk-task plm-card${c.priority === 'high' && OPEN.includes(c.stage) ? ' high' : ''}`}>
      <button className="tk-task-main" onClick={onOpen}>
        <span className="mt-card-top small">
          <span className="muted tk-task-no">{c.name}</span>
          {c.priority === 'high' && OPEN.includes(c.stage) ? <span className="mt-chip urgent">{t('plm.high')}</span> : null}
          {c.behind ? (
            <span className="mt-chip high">
              <Icon name="alert" size={12} /> {t('plm.behind_chip')}
            </span>
          ) : null}
        </span>
        <strong>{c.title}</strong>
        <span className="small muted mt-card-where">
          <Icon name="book" size={12} /> {c.bom_name}
        </span>
        <span className="tk-task-meta small">
          <span className="muted plm-ver">v{c.applied_version ?? c.base_version}</span>
          {c.stage === 'review' ? (
            <span className="muted">
              <Icon name="checkCircle" size={12} /> {t('plm.approvals_of', { n: c.approvals, of: settings.approvals_needed })}
            </span>
          ) : null}
          {c.stage === 'approved' && c.effective_on ? (
            <span className="muted">
              <Icon name="calendar" size={12} /> {dayText(c.effective_on)}
            </span>
          ) : null}
          {c.stage === 'done' && c.applied_at ? <span className="muted">{dayText(c.applied_at)}</span> : null}
          <span className="tk-avatar" title={c.responsible_name ?? t('plm.nobody')} aria-label={c.responsible_name ?? t('plm.nobody')}>
            {initials(c.responsible_name)}
          </span>
        </span>
      </button>
    </article>
  );
}

function useSearch() {
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearch(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  return { q, setQ, search };
}

function BoardTab({ today, settings, onOpen, onNew }: { today: string; settings: Settings; onOpen: (id: string) => void; onNew?: () => void }) {
  const { q, setQ, search } = useSearch();
  const { data } = useAction<{ today: string; changes: Row[] }>('plm.list', { view: 'board', search: search || null });
  return (
    <>
      <div className="toolbar tk-board-tools mt-tools">
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('plm.search')} aria-label={t('plm.search')} onChange={(e) => setQ(e.target.value)} />
        </label>
      </div>
      {!data ? (
        <Skeleton h={320} r="var(--radius-lg)" />
      ) : !data.changes.length && !search ? (
        <EmptyState
          icon="branch"
          title={t('plm.empty_title')}
          body={t('plm.empty_body')}
          action={
            onNew ? (
              <Button variant="primary" icon="plus" onClick={onNew}>
                {t('plm.new')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="tk-board mt-board plm-board" style={{ '--cols': COLUMNS.length } as React.CSSProperties}>
          {COLUMNS.map((s) => {
            const cards = data.changes.filter((c) => c.stage === s);
            return (
              <section key={s} className={`tk-col plm-col-${s}`} aria-label={t(`plm.col_${s}` as MessageKey)}>
                <header className="tk-col-head">
                  <Icon name={STAGE[s].icon} size={16} />
                  <strong>{t(`plm.col_${s}` as MessageKey)}</strong>
                  <span className="muted small">{cards.length}</span>
                </header>
                <div className="tk-col-body">
                  {cards.map((c) => (
                    <ChangeCard key={c.id} c={c} settings={settings} onOpen={() => onOpen(c.id)} />
                  ))}
                  {!cards.length ? <p className="muted small tk-col-empty">{t(`plm.col_empty_${s}` as MessageKey)}</p> : null}
                  {s === 'approved' && cards.some((c) => c.effective_on && c.effective_on > today) ? <p className="muted small tk-col-empty">{t('plm.col_scheduled_note')}</p> : null}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}

function BomsTab({ onOpen }: { onOpen: (id: string) => void }) {
  const { data } = useAction<BomRow[]>('plm.boms');
  const { q, setQ, search } = useSearch();
  const rows = useMemo(() => (data ?? []).filter((b) => !search || `${b.name} ${b.item_name}`.toLowerCase().includes(search.toLowerCase())), [data, search]);
  return (
    <>
      <div className="toolbar mt-tools">
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('plm.search_boms')} aria-label={t('plm.search_boms')} onChange={(e) => setQ(e.target.value)} />
        </label>
      </div>
      {!data ? (
        <Skeleton h={300} />
      ) : !data.length ? (
        <EmptyState icon="book" title={t('plm.no_boms')} body={t('plm.no_boms_body')} />
      ) : (
        <TableWrap>
          <Table
            head={[t('plm.c_bom'), t('plm.c_product'), t('plm.c_version'), t('plm.c_open'), t('plm.c_applied'), t('plm.c_last_change')]}
            phoneHide={[1, 4, 5]}
            empty={t('plm.no_match')}
            rows={rows.map((b) => ({
              key: b.id,
              onClick: () => onOpen(b.id),
              cells: [
                <span key="n">
                  <strong>{b.name}</strong>
                  {!b.active ? <span className="muted small"> · {t('plm.not_in_use')}</span> : null}
                </span>,
                b.item_name,
                <span key="v" className="plm-ver">
                  v{b.version}
                </span>,
                b.open ? <span className="mt-chip high">{b.open}</span> : '—',
                b.applied || '—',
                b.changed_at ? dayText(b.changed_at) : '—',
              ],
            }))}
          />
        </TableWrap>
      )}
    </>
  );
}

const VIEWS = ['open', 'all'] as const;

function ListTab({ onOpen }: { onOpen: (id: string) => void }) {
  const [view, setView] = useState<(typeof VIEWS)[number]>('open');
  const { q, setQ, search } = useSearch();
  const { data } = useAction<{ today: string; changes: Row[] }>('plm.list', { view, search: search || null });
  return (
    <>
      <div className="toolbar mt-tools">
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('plm.search')} aria-label={t('plm.search')} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="segmented" role="radiogroup" aria-label={t('plm.show')}>
          {VIEWS.map((v) => (
            <button key={v} type="button" role="radio" aria-checked={view === v} className={view === v ? 'active' : ''} onClick={() => setView(v)}>
              {t(`plm.view_${v}` as MessageKey)}
            </button>
          ))}
        </div>
        <Button
          size="sm"
          icon="download"
          disabled={!data?.changes.length}
          onClick={() =>
            data &&
            downloadCsv(
              'product-changes.csv',
              [t('plm.c_number'), t('plm.c_title'), t('plm.c_bom'), t('plm.c_stage'), t('plm.c_priority'), t('plm.c_responsible'), t('plm.c_effective'), t('plm.c_created'), t('plm.c_applied_on')],
              data.changes.map((c) => [
                c.name,
                c.title,
                c.bom_name,
                t(`plm.st_${c.stage}` as MessageKey),
                t(`plm.pr_${c.priority}` as MessageKey),
                c.responsible_name ?? '',
                c.effective_on ?? '',
                c.created_at.slice(0, 10),
                c.applied_at?.slice(0, 10) ?? '',
              ]),
            )
          }
        >
          {t('plm.csv')}
        </Button>
      </div>
      {!data ? (
        <Skeleton h={300} />
      ) : (
        <TableWrap>
          <Table
            view="plm.list"
            head={[t('plm.c_number'), t('plm.c_title'), t('plm.c_bom'), t('plm.c_stage'), t('plm.c_responsible'), t('plm.c_updated')]}
            phoneHide={[2, 4, 5]}
            empty={search ? t('plm.no_match') : t('plm.none_here')}
            rows={data.changes.map((c) => ({
              key: c.id,
              onClick: () => onOpen(c.id),
              cells: [
                <strong key="n">{c.name}</strong>,
                <span key="t" className="plm-title-cell">
                  {c.title}
                  {c.priority === 'high' && OPEN.includes(c.stage) ? <span className="mt-chip urgent">{t('plm.high')}</span> : null}
                </span>,
                c.bom_name,
                <StageBadge key="s" c={c} />,
                c.responsible_name ?? '—',
                dayText(c.updated_at),
              ],
            }))}
          />
        </TableWrap>
      )}
    </>
  );
}

function SettingsTab({ approvers, onChange }: { approvers: { id: string; name: string }[]; onChange: () => Promise<void> }) {
  const { data: o } = useAction<Overview>('plm.overview');
  const [f, setF] = useState<Settings | null>(null);
  useEffect(() => {
    if (o) setF(o.settings);
  }, [o]);
  const { busy, run } = useSave();
  if (!f || !o) return <Skeleton h={240} />;
  const tooMany = f.approvals_needed > Math.max(1, approvers.length - (f.self_approval ? 0 : 1));
  const save = () =>
    void run(async () => {
      await action('plm.save_settings', { ...f });
      await onChange();
    }, t('plm.settings_saved'));
  return (
    <section className="card form-stack plm-settings">
      <h2>{t('plm.approval_rules')}</h2>
      <p className="muted">{t('plm.approval_rules_hint')}</p>
      <Field
        label={t('plm.approvals_needed')}
        hint={tooMany ? undefined : t('plm.approvals_needed_hint', { names: approvers.map((a) => a.name).join(', ') || '—' })}
        error={tooMany ? t('plm.e_too_many', { n: approvers.length }) : null}
      >
        <input
          className="input plm-num"
          type="number"
          min={1}
          max={10}
          value={f.approvals_needed}
          onChange={(e) => setF({ ...f, approvals_needed: Math.max(1, Math.min(10, Number(e.target.value) || 1)) })}
        />
      </Field>
      <div className="setting-row">
        <div>
          <strong>{t('plm.self_approval')}</strong>
          <p className="muted small">{t('plm.self_approval_hint')}</p>
        </div>
        <Switch checked={f.self_approval} label={t('plm.self_approval')} onChange={(v) => setF({ ...f, self_approval: v })} />
      </div>
      <p className="callout callout-info">
        <Icon name="shield" size={16} />
        <span>{t('plm.direct_edit_note')}</span>
      </p>
      <div>
        <Button variant="primary" icon="check" busy={busy} onClick={save}>
          {t('plm.save_settings')}
        </Button>
      </div>
    </section>
  );
}

// ---- what changes -------------------------------------------------------------------------------

const KIND_TONE: Record<'added' | 'removed' | 'changed', string> = { added: 'plm-add', removed: 'plm-del', changed: 'plm-mod' };
const KIND_ICON: Record<'added' | 'removed' | 'changed', IconName> = { added: 'plus', removed: 'minus', changed: 'edit' };

function headerValue(field: Diff['header'][number]['field'], v: number | string | null) {
  if (v === null || v === '') return t('plm.empty_value');
  if (field === 'overhead') return money(Number(v));
  if (field === 'output_qty') return num(Number(v));
  return String(v);
}

/** What is different, line by line: green is new, red is taken out, amber is changed. */
function DiffView({ diff, names, empty }: { diff: Diff; names: Names; empty?: string }) {
  const item = (id: string) => names.items[id];
  const centre = (id: string | null) => (id ? (names.workcenters[id] ?? '—') : t('plm.no_centre'));
  const qty = (id: string, v: { qty: number; scrap_pct: number }) => `${num(v.qty)} ${item(id)?.unit ?? ''}${v.scrap_pct ? ` · ${t('plm.scrap', { pct: num(v.scrap_pct) })}` : ''}`;
  const step = (v: { workcenter: string | null; minutes: number }) => `${tn('plm.n_minutes', v.minutes)} · ${centre(v.workcenter)}`;
  if (!diff.count) return <p className="muted small">{empty ?? t('plm.no_difference')}</p>;
  const Arrow = ({ from, to }: { from: string | null; to: string | null }) => (
    <span className="plm-arrow">
      {from !== null ? <del>{from}</del> : null}
      {from !== null && to !== null ? <Icon name="chevronRight" size={12} /> : null}
      {to !== null ? <ins>{to}</ins> : null}
    </span>
  );
  return (
    <div className="plm-diff">
      {diff.header.length ? (
        <ul aria-label={t('plm.batch')}>
          {diff.header.map((h) => (
            <li key={h.field} className="plm-mod">
              <Icon name="edit" size={14} />
              <span className="plm-diff-name">{t(`plm.f_${h.field}` as MessageKey)}</span>
              <Arrow from={headerValue(h.field, h.from)} to={headerValue(h.field, h.to)} />
            </li>
          ))}
        </ul>
      ) : null}
      {diff.lines.length ? (
        <>
          <h4 className="plm-h4">{t('plm.materials')}</h4>
          <ul aria-label={t('plm.materials')}>
            {diff.lines.map((l) => (
              <li key={`${l.item}-${l.kind}`} className={KIND_TONE[l.kind]}>
                <Icon name={KIND_ICON[l.kind]} size={14} />
                <span className="plm-diff-name">
                  {item(l.item)?.name ?? '—'}
                  <span className="sr-only"> · {t(`plm.k_${l.kind}` as MessageKey)}</span>
                </span>
                <Arrow from={l.from ? qty(l.item, l.from) : null} to={l.to ? qty(l.item, l.to) : null} />
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {diff.steps.length || diff.order ? (
        <>
          <h4 className="plm-h4">{t('plm.steps')}</h4>
          <ul aria-label={t('plm.steps')}>
            {diff.steps.map((s, i) => (
              <li key={`${s.name}-${s.kind}-${i}`} className={KIND_TONE[s.kind]}>
                <Icon name={KIND_ICON[s.kind]} size={14} />
                <span className="plm-diff-name">
                  {s.name}
                  <span className="sr-only"> · {t(`plm.k_${s.kind}` as MessageKey)}</span>
                </span>
                <Arrow from={s.from ? step(s.from) : null} to={s.to ? step(s.to) : null} />
              </li>
            ))}
            {diff.order ? (
              <li className="plm-mod">
                <Icon name="move" size={14} />
                <span className="plm-diff-name">{t('plm.order_changed')}</span>
              </li>
            ) : null}
          </ul>
        </>
      ) : null}
    </div>
  );
}

/** What a batch costs before and after, at today's stock costs and work centre rates. */
function CostCompare({ base, next, unit }: { base: Cost; next: Cost; unit: string }) {
  const rows: [MessageKey, number, number][] = [
    ['plm.cost_materials', base.materials, next.materials],
    ['plm.cost_labour', base.labour, next.labour],
    ['plm.cost_overhead', base.overhead, next.overhead],
    ['plm.cost_total', base.total, next.total],
  ];
  const delta = next.per_unit - base.per_unit;
  const pct = base.per_unit ? Math.round((delta / base.per_unit) * 1000) / 10 : null;
  return (
    <div className="plm-cost">
      <table className="plm-cost-table">
        <thead>
          <tr>
            <th scope="col">
              <span className="sr-only">{t('plm.cost')}</span>
            </th>
            <th scope="col">{t('plm.now')}</th>
            <th scope="col">{t('plm.after')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([k, a, b]) => (
            <tr key={k} className={k === 'plm.cost_total' ? 'plm-cost-sum' : undefined}>
              <th scope="row">{t(k)}</th>
              <td className="tabular">{money(a)}</td>
              <td className={`tabular${a !== b ? ' plm-cost-moved' : ''}`}>{money(b)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className={`plm-unit${delta > 0 ? ' up' : delta < 0 ? ' down' : ''}`}>
        <span className="muted small">{t('plm.per_unit', { unit: unit || t('plm.unit') })}</span>
        <strong className="tabular">{money(next.per_unit)}</strong>
        <span className="small">
          {delta === 0 ? (
            t('plm.cost_same')
          ) : (
            <>
              <Icon name={delta > 0 ? 'trendUp' : 'trendDown'} size={14} /> {money(delta, { sign: true })}
              {pct !== null ? ` (${pct > 0 ? '+' : ''}${num(pct)} %)` : ''}
            </>
          )}
        </span>
      </div>
    </div>
  );
}

/** The whole recipe a change proposes, so the reader sees it as the kitchen will. */
function ContentView({ c, names }: { c: Content; names: Names }) {
  return (
    <div className="plm-content">
      <p className="small muted">
        {t('plm.makes', { qty: num(c.output_qty) })} · {t('plm.f_overhead')} {money(c.overhead)}
      </p>
      <ul className="plm-content-list">
        {c.lines.map((l) => (
          <li key={l.item}>
            <Icon name="box" size={14} />
            <span>{names.items[l.item]?.name ?? '—'}</span>
            <span className="tabular">
              {num(l.qty)} {names.items[l.item]?.unit ?? ''}
              {l.scrap_pct ? <span className="muted small"> · {t('plm.scrap', { pct: num(l.scrap_pct) })}</span> : null}
            </span>
          </li>
        ))}
      </ul>
      {c.steps.length ? (
        <ol className="plm-content-list plm-steps">
          {c.steps.map((s, i) => (
            <li key={i}>
              <span className="plm-step-no">{i + 1}</span>
              <span>{s.name}</span>
              <span className="muted small">
                {tn('plm.n_minutes', s.minutes)} · {s.workcenter ? (names.workcenters[s.workcenter] ?? '—') : t('plm.no_centre')}
              </span>
            </li>
          ))}
        </ol>
      ) : null}
      {c.note ? <p className="mt-desc">{c.note}</p> : null}
    </div>
  );
}

// ---- one change ---------------------------------------------------------------------------------

type StepKind = 'edit' | 'details' | 'approve' | 'reject' | 'cancel';

function ChangeDialog({
  id,
  setup,
  editFirst,
  onClose,
  onChange,
  onBom,
}: {
  id: string;
  setup: Setup;
  editFirst: boolean;
  onClose: () => void;
  onChange: () => Promise<void>;
  onBom: (id: string) => void;
}) {
  const { data: fetched } = useAction<Detail>('plm.get', { id });
  const [c, setC] = useState<Detail | null>(null);
  useEffect(() => setC(fetched), [fetched]);
  const { busy, run } = useSave();
  const [step, setStep] = useState<StepKind | null>(editFirst ? 'edit' : null);
  const [showAll, setShowAll] = useState(false);
  const after = async (next: Detail) => {
    setC(next);
    setStep(null);
    await onChange();
  };
  const go = (name: string, done: string) =>
    void run(async () => {
      await after(await action<Detail>(name, { id }));
    }, done);

  if (!c)
    return (
      <Dialog title={t('plm.change')} onClose={onClose} size="lg">
        <Skeleton h={420} />
      </Dialog>
    );
  const unit = setup.boms.find((b) => b.id === c.bom)?.unit ?? '';
  const current = c.approvals.filter((a) => a.current);
  const earlier = c.approvals.filter((a) => !a.current);
  const yes = current.filter((a) => a.decision === 'approved').length;
  const moves: React.ReactNode[] = [];
  const add = (key: string, node: React.ReactNode) => moves.push(<span key={key}>{node}</span>);
  if (c.can.rebase)
    add(
      'rebase',
      <Button variant="primary" icon="undo" busy={busy} onClick={() => go('plm.rebase', t('plm.rebased'))}>
        {t('plm.rebase')}
      </Button>,
    );
  if (c.can.submit && !c.conflict)
    add(
      'submit',
      <Button variant="primary" icon="send" busy={busy} disabled={!c.diff.count} onClick={() => go('plm.submit', t('plm.submitted'))}>
        {t('plm.submit')}
      </Button>,
    );
  if (c.can.decide && !c.conflict) {
    add(
      'reject',
      <Button icon="x" onClick={() => setStep('reject')}>
        {t('plm.reject')}
      </Button>,
    );
    add(
      'approve',
      <Button variant="success" icon="check" onClick={() => setStep('approve')}>
        {t('plm.approve')}
      </Button>,
    );
  }
  if (c.can.apply && !c.conflict)
    add(
      'apply',
      <Button variant="primary" icon="checkCircle" busy={busy} disabled={!c.due} onClick={() => go('plm.apply', t('plm.applied_done', { v: c.base_version + 1 }))}>
        {t('plm.apply')}
      </Button>,
    );

  return (
    <>
      <Dialog
        title={`${c.name} · ${c.title}`}
        onClose={onClose}
        size="lg"
        footer={
          <span className="foot-actions rp-foot">
            {c.can.edit ? (
              <Button variant="ghost" icon="edit" onClick={() => setStep('details')}>
                {t('plm.edit_details')}
              </Button>
            ) : null}
            {c.can.cancel ? (
              <Button variant="ghost" icon="x" onClick={() => setStep('cancel')}>
                {t('plm.cancel')}
              </Button>
            ) : null}
            <span className="mt-foot-moves">{moves}</span>
          </span>
        }
      >
        <div className="mt-detail">
          <div className="mt-badges">
            <StageBadge c={c} />
            {c.priority === 'high' && OPEN.includes(c.stage) ? <StatusBadge tone="danger" icon="flame" label={t('plm.high')} /> : null}
            {c.stage === 'done' && c.applied_version ? <StatusBadge tone="ok" icon="book" label={t('plm.became_version', { v: c.applied_version })} /> : null}
          </div>
          {c.conflict ? (
            <div className="callout callout-warn">
              <Icon name="alert" size={16} />
              <div>
                <p>
                  <strong>{t('plm.conflict_title')}</strong>
                </p>
                <p>{c.can.rebase ? t('plm.conflict_body') : t('plm.conflict_body_view')}</p>
                {c.conflict_diff ? <DiffView diff={c.conflict_diff} names={c.names} /> : null}
              </div>
            </div>
          ) : null}
          {!c.conflict && c.stage === 'draft' ? (
            <p className="callout callout-info">
              <Icon name="pen" size={16} />
              <span>{!c.diff.count ? t('plm.hint_empty') : c.round > 0 ? t('plm.hint_sent_back') : tn('plm.hint_draft', c.settings.approvals_needed)}</span>
            </p>
          ) : null}
          {!c.conflict && c.stage === 'review' ? (
            <p className={`callout ${c.can.decide ? 'callout-warn' : 'callout-info'}`}>
              <Icon name="eye" size={16} />
              <span>
                {c.can.decide ? t('plm.hint_decide') : c.can.own_review ? t('plm.hint_own') : c.can.decided ? t('plm.hint_decided') : t('plm.hint_review', { n: yes, of: c.settings.approvals_needed })}
              </span>
            </p>
          ) : null}
          {!c.conflict && c.stage === 'approved' ? (
            <p className={`callout ${c.due ? 'callout-ok' : 'callout-info'}`}>
              <Icon name={c.due ? 'checkCircle' : 'calendar'} size={16} />
              <span>
                {c.due ? t('plm.hint_due', { v: c.base_version + 1 }) : t('plm.hint_scheduled', { day: dayText(c.effective_on!) })}
                {c.due && c.planned_orders ? ` ${tn('plm.hint_planned', c.planned_orders)}` : ''}
              </span>
            </p>
          ) : null}
          <dl className="mt-facts">
            <div>
              <dt>{t('plm.bom')}</dt>
              <dd>
                <button type="button" className="link" onClick={() => onBom(c.bom)}>
                  {c.bom_name}
                </button>
                <br />
                <span className="muted small">
                  {c.item_name} · {c.stage === 'done' ? t('plm.from_to_version', { a: c.base_version, b: c.applied_version ?? c.base_version + 1 }) : t('plm.on_version', { v: c.base_version })}
                </span>
              </dd>
            </div>
            <div>
              <dt>{t('plm.responsible')}</dt>
              <dd>{c.responsible_name ?? t('plm.nobody')}</dd>
            </div>
            <div>
              <dt>{t('plm.proposed_by')}</dt>
              <dd>
                {c.created_by_name ?? '—'}
                <br />
                <span className="muted small">{dateTime(c.created_at)}</span>
              </dd>
            </div>
            <div>
              <dt>{t('plm.effective_on')}</dt>
              <dd>{c.effective_on ? dayText(c.effective_on) : t('plm.when_approved')}</dd>
            </div>
            {c.stage === 'review' || c.stage === 'approved' ? (
              <div>
                <dt>{t('plm.approvals')}</dt>
                <dd>{t('plm.approvals_of', { n: yes, of: c.settings.approvals_needed })}</dd>
              </div>
            ) : null}
            {c.applied_at ? (
              <div>
                <dt>{t('plm.applied_on')}</dt>
                <dd>{dateTime(c.applied_at)}</dd>
              </div>
            ) : null}
          </dl>
          <section>
            <h3 className="mt-h3">{t('plm.reason')}</h3>
            <p className="mt-desc">{c.reason}</p>
          </section>
          <section>
            <div className="mt-section-head">
              <h3 className="mt-h3">{tn('plm.what_changes', c.diff.count)}</h3>
              {c.can.edit ? (
                <Button size="sm" icon="edit" onClick={() => setStep('edit')}>
                  {c.diff.count ? t('plm.edit_content') : t('plm.write_content')}
                </Button>
              ) : null}
            </div>
            <DiffView diff={c.diff} names={c.names} empty={c.can.edit ? t('plm.no_difference_edit') : undefined} />
            <button type="button" className="link small plm-toggle" aria-expanded={showAll} onClick={() => setShowAll((v) => !v)}>
              <Icon name={showAll ? 'up' : 'down'} size={12} /> {showAll ? t('plm.hide_full') : t('plm.show_full')}
            </button>
            {showAll ? <ContentView c={c.proposed} names={c.names} /> : null}
          </section>
          {c.diff.count ? (
            <section>
              <h3 className="mt-h3">{t('plm.cost_title')}</h3>
              <CostCompare base={c.cost.base} next={c.cost.proposed} unit={unit} />
              <p className="muted small">{t('plm.cost_hint')}</p>
            </section>
          ) : null}
          {c.approvals.length ? (
            <section>
              <h3 className="mt-h3">{t('plm.approvals')}</h3>
              <ul className="plm-approvals">
                {[...current, ...earlier].map((a, i) => (
                  <li key={i} className={a.current ? undefined : 'plm-old'}>
                    <StatusBadge tone={a.decision === 'approved' ? 'ok' : 'danger'} icon={a.decision === 'approved' ? 'check' : 'x'} label={t(`plm.d_${a.decision}` as MessageKey)} />
                    <span>
                      <strong>{a.user_name}</strong>
                      {a.note ? <span> · {a.note}</span> : null}
                      <br />
                      <span className="muted small">
                        {dateTime(a.at)}
                        {!a.current ? ` · ${t('plm.earlier_round', { n: a.round })}` : ''}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {c.cancel_reason ? (
            <section>
              <h3 className="mt-h3">{t('plm.cancelled_why')}</h3>
              <p className="mt-desc">{c.cancel_reason}</p>
            </section>
          ) : null}
          <section>
            <h3 className="mt-h3">{t('plm.history')}</h3>
            <ol className="mt-events">
              {c.events.map((e, i) => (
                <li key={i}>
                  <span className="mt-event-dot" aria-hidden="true" />
                  <span>
                    <strong>{t(`plm.ev_${e.kind}` as MessageKey)}</strong>
                    {e.text ? <span> · {e.text}</span> : null}
                    <br />
                    <span className="muted small">{[dateTime(e.at), e.by_name].filter(Boolean).join(' · ')}</span>
                  </span>
                </li>
              ))}
            </ol>
            <NoteAdd id={c.id} onDone={after} />
          </section>
        </div>
      </Dialog>
      {step === 'edit' ? <ContentEditor c={c} setup={setup} onClose={() => setStep(null)} onDone={after} /> : null}
      {step === 'details' ? <DetailsEditor c={c} setup={setup} onClose={() => setStep(null)} onDone={after} /> : null}
      {step === 'approve' || step === 'reject' || step === 'cancel' ? <DecideStep c={c} step={step} onClose={() => setStep(null)} onDone={after} /> : null}
    </>
  );
}

function NoteAdd({ id, onDone }: { id: string; onDone: (c: Detail) => Promise<void> }) {
  const [text, setText] = useState('');
  const { busy, run } = useSave();
  const save = () =>
    text.trim() &&
    void run(async () => {
      await onDone(await action<Detail>('plm.add_note', { id, note: text.trim() }));
      setText('');
    });
  return (
    <div className="mt-note-add">
      <input
        className="input"
        value={text}
        maxLength={300}
        placeholder={t('plm.note_placeholder')}
        aria-label={t('plm.note_placeholder')}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && save()}
      />
      <Button size="sm" icon="plus" busy={busy} disabled={!text.trim()} onClick={save}>
        {t('plm.add_note')}
      </Button>
    </div>
  );
}

/** Approve (a note is welcome), send back (the note says what to try) or cancel (why). */
function DecideStep({ c, step, onClose, onDone }: { c: Detail; step: 'approve' | 'reject' | 'cancel'; onClose: () => void; onDone: (c: Detail) => Promise<void> }) {
  const [text, setText] = useState('');
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const needed = step !== 'approve';
  const error = tried && needed && text.trim().length < 3 ? t(`plm.e_${step}` as MessageKey) : null;
  const save = () => {
    setTried(true);
    if (needed && text.trim().length < 3) return;
    void run(
      async () => {
        const input = step === 'cancel' ? { id: c.id, reason: text.trim() } : { id: c.id, decision: step === 'approve' ? 'approved' : 'rejected', note: text.trim() || null };
        await onDone(await action<Detail>(step === 'cancel' ? 'plm.cancel' : 'plm.decide', input));
      },
      t(`plm.done_${step}` as MessageKey),
    );
  };
  return (
    <Dialog
      title={t(`plm.title_${step}` as MessageKey, { name: c.name })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button variant={step === 'cancel' ? 'danger' : step === 'approve' ? 'success' : 'primary'} icon={step === 'approve' ? 'check' : 'x'} busy={busy} onClick={save}>
            {t(`plm.go_${step}` as MessageKey)}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <p className="muted">{t(`plm.ask_${step}` as MessageKey)}</p>
        <Field label={t(`plm.label_${step}` as MessageKey)} required={needed} error={error} counter={`${text.length}/300`}>
          <textarea className="input" rows={3} maxLength={300} value={text} onChange={(e) => setText(e.target.value)} data-autofocus />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- writing a change ----------------------------------------------------------------------------

interface HeadForm {
  title: string;
  reason: string;
  priority: 'normal' | 'high';
  responsible: string;
  effective_on: string;
}

function HeadFields({
  f,
  set,
  people,
  errors,
  today,
}: {
  f: HeadForm;
  set: (k: keyof HeadForm, v: string) => void;
  people: Setup['people'];
  errors: Partial<Record<keyof HeadForm, string>>;
  today: string;
}) {
  return (
    <>
      <Field label={t('plm.f_title')} required error={errors.title} counter={`${f.title.length}/120`}>
        <input className="input" value={f.title} maxLength={120} placeholder={t('plm.title_placeholder')} onChange={(e) => set('title', e.target.value)} />
      </Field>
      <Field label={t('plm.reason')} required error={errors.reason} hint={t('plm.reason_hint')}>
        <textarea className="input" rows={3} value={f.reason} maxLength={4000} onChange={(e) => set('reason', e.target.value)} />
      </Field>
      <div className="form-grid plm-head-grid">
        <Field label={t('plm.priority')}>
          <div className="segmented" role="radiogroup" aria-label={t('plm.priority')}>
            {(['normal', 'high'] as const).map((p) => (
              <button key={p} type="button" role="radio" aria-checked={f.priority === p} className={f.priority === p ? 'active' : ''} onClick={() => set('priority', p)}>
                {t(`plm.pr_${p}` as MessageKey)}
              </button>
            ))}
          </div>
        </Field>
        <Field label={t('plm.responsible')}>
          <select className="input" value={f.responsible} onChange={(e) => set('responsible', e.target.value)}>
            <option value="">{t('plm.nobody')}</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('plm.effective_on')} hint={t('plm.effective_hint')} error={errors.effective_on}>
          <input className="input" type="date" min={today} value={f.effective_on} onChange={(e) => set('effective_on', e.target.value)} />
        </Field>
      </div>
    </>
  );
}

const headErrors = (f: HeadForm, today: string) => {
  const e: Partial<Record<keyof HeadForm, string>> = {};
  if (f.title.trim().length < 3) e.title = t('plm.e_title');
  if (f.reason.trim().length < 3) e.reason = t('plm.e_reason');
  if (f.effective_on && f.effective_on < today) e.effective_on = t('plm.e_past');
  return e;
};
const localDay = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** A new change on a bill of materials, or one that brings an old version back. */
function NewChange({ setup, bom, restore, onClose, onSaved }: { setup: Setup; bom: string | null; restore: number | null; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { me } = useSession();
  const today = localDay();
  const [pick, setPick] = useState(bom ?? '');
  const [f, setF] = useState<HeadForm>({
    title: restore ? t('plm.restore_title', { v: restore }) : '',
    reason: '',
    priority: 'normal',
    responsible: setup.people.some((p) => p.id === me?.userId) ? (me?.userId ?? '') : '',
    effective_on: '',
  });
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const errors = tried ? headErrors(f, today) : {};
  const bomError = tried && !pick ? t('plm.e_bom') : null;
  const chosen = setup.boms.find((b) => b.id === pick);
  const save = () => {
    setTried(true);
    if (!pick || Object.keys(headErrors(f, today)).length) return;
    void run(async () => {
      const head = { title: f.title.trim(), reason: f.reason.trim(), priority: f.priority, responsible: f.responsible || null, effective_on: f.effective_on || null };
      const c = await action<Detail>(restore ? 'plm.restore' : 'plm.create', restore ? { bom: pick, version: restore, ...head } : { bom: pick, ...head });
      await onSaved(c.id);
    }, t('plm.created'));
  };
  return (
    <Dialog
      title={restore ? t('plm.restore_heading', { v: restore }) : t('plm.new')}
      onClose={onClose}
      size="md"
      footer={
        <span className="foot-actions">
          <Button variant="primary" icon={restore ? 'undo' : 'plus'} busy={busy} onClick={save}>
            {restore ? t('plm.restore_go') : t('plm.create_go')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        {restore ? <p className="callout callout-info">{t('plm.restore_hint', { v: restore })}</p> : null}
        <Field label={t('plm.bom')} required error={bomError} hint={chosen ? t('plm.bom_hint', { product: chosen.item_name, v: chosen.version }) : undefined}>
          <select className="input" value={pick} disabled={!!bom} onChange={(e) => setPick(e.target.value)} data-autofocus={!bom || undefined}>
            <option value="">{t('plm.choose_bom')}</option>
            {setup.boms.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} · v{b.version}
              </option>
            ))}
          </select>
        </Field>
        <HeadFields f={f} set={(k, v) => setF((x) => ({ ...x, [k]: v }))} people={setup.people} errors={errors} today={today} />
      </div>
    </Dialog>
  );
}

function DetailsEditor({ c, setup, onClose, onDone }: { c: Detail; setup: Setup; onClose: () => void; onDone: (c: Detail) => Promise<void> }) {
  const [f, setF] = useState<HeadForm>({ title: c.title, reason: c.reason, priority: c.priority, responsible: c.responsible ?? '', effective_on: c.effective_on ?? '' });
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const errors = tried ? headErrors(f, c.today) : {};
  const save = () => {
    setTried(true);
    if (Object.keys(headErrors(f, c.today)).length) return;
    void run(async () => {
      await onDone(
        await action<Detail>('plm.save', {
          id: c.id,
          title: f.title.trim(),
          reason: f.reason.trim(),
          priority: f.priority,
          responsible: f.responsible || null,
          effective_on: f.effective_on || null,
          proposed: c.proposed,
        }),
      );
    }, t('plm.saved'));
  };
  return (
    <Dialog
      title={t('plm.edit_details_title', { name: c.name })}
      onClose={onClose}
      size="md"
      footer={
        <span className="foot-actions">
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('plm.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <HeadFields f={f} set={(k, v) => setF((x) => ({ ...x, [k]: v }))} people={setup.people} errors={errors} today={c.today} />
      </div>
    </Dialog>
  );
}

interface EditLine {
  key: number;
  item: string;
  qty: string;
  scrap: string;
}
interface EditStep {
  key: number;
  name: string;
  workcenter: string;
  minutes: string;
}
let nextKey = 1;
const toNumber = (s: string) => {
  const n = Number(s.replace(',', '.'));
  return s.trim() === '' || !Number.isFinite(n) ? null : n;
};

/** The recipe as the change proposes it: materials, work steps and the batch. Saved as one. */
function ContentEditor({ c, setup, onClose, onDone }: { c: Detail; setup: Setup; onClose: () => void; onDone: (c: Detail) => Promise<void> }) {
  const p = c.proposed;
  const [output, setOutput] = useState(String(p.output_qty));
  const [overhead, setOverhead] = useState((p.overhead / 100).toFixed(2));
  const [note, setNote] = useState(p.note ?? '');
  const [lines, setLines] = useState<EditLine[]>(p.lines.map((l) => ({ key: nextKey++, item: l.item, qty: String(l.qty), scrap: l.scrap_pct ? String(l.scrap_pct) : '' })));
  const [steps, setSteps] = useState<EditStep[]>(p.steps.map((s) => ({ key: nextKey++, name: s.name, workcenter: s.workcenter ?? '', minutes: String(s.minutes) })));
  const [add, setAdd] = useState('');
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const bom = setup.boms.find((b) => b.id === c.bom);
  const items = useMemo(() => new Map(setup.items.map((i) => [i.id, i])), [setup.items]);
  const free = setup.items.filter((i) => i.id !== bom?.item && !lines.some((l) => l.item === i.id));
  // What the materials and steps cost per batch as typed, so the cook sees it before saving.
  const estimate = useMemo(() => {
    let m = 0;
    for (const l of lines) {
      const q = toNumber(l.qty) ?? 0;
      m += q * (1 + (toNumber(l.scrap) ?? 0) / 100) * (items.get(l.item)?.unit_cost ?? 0);
    }
    let w = 0;
    for (const s of steps) w += ((toNumber(s.minutes) ?? 0) / 60) * (setup.workcenters.find((x) => x.id === s.workcenter)?.cost_per_hour ?? 0);
    const total = Math.round(m) + Math.round(w) + (parseMoney(overhead) ?? 0);
    const out = toNumber(output) ?? 0;
    return { total, per_unit: out > 0 ? Math.round(total / out) : 0 };
  }, [lines, steps, overhead, output, items, setup.workcenters]);

  const outErr = tried && !((toNumber(output) ?? 0) > 0) ? t('plm.e_output') : null;
  const ohErr = tried && parseMoney(overhead) === null && overhead.trim() !== '' ? t('plm.e_money') : null;
  const lineErr = (l: EditLine) => tried && !((toNumber(l.qty) ?? 0) > 0);
  const scrapErr = (l: EditLine) => tried && l.scrap.trim() !== '' && !((toNumber(l.scrap) ?? -1) >= 0 && (toNumber(l.scrap) ?? 101) <= 100);
  const stepErr = (s: EditStep) => tried && (!s.name.trim() || toNumber(s.minutes) === null || (toNumber(s.minutes) ?? 0) < 0);
  const linesErr = tried && !lines.length ? t('plm.e_no_lines') : null;
  const bad = () =>
    !((toNumber(output) ?? 0) > 0) ||
    (parseMoney(overhead) === null && overhead.trim() !== '') ||
    !lines.length ||
    lines.some((l) => !((toNumber(l.qty) ?? 0) > 0) || (l.scrap.trim() !== '' && !((toNumber(l.scrap) ?? -1) >= 0 && (toNumber(l.scrap) ?? 101) <= 100))) ||
    steps.some((s) => !s.name.trim() || toNumber(s.minutes) === null || (toNumber(s.minutes) ?? 0) < 0);
  const save = () => {
    setTried(true);
    if (bad()) return;
    void run(async () => {
      await onDone(
        await action<Detail>('plm.save', {
          id: c.id,
          title: c.title,
          reason: c.reason,
          priority: c.priority,
          responsible: c.responsible,
          effective_on: c.effective_on,
          proposed: {
            output_qty: toNumber(output),
            overhead: parseMoney(overhead) ?? 0,
            note: note.trim() || null,
            lines: lines.map((l) => ({ item: l.item, qty: toNumber(l.qty), scrap_pct: toNumber(l.scrap) ?? 0 })),
            steps: steps.map((s) => ({ name: s.name.trim(), workcenter: s.workcenter || null, minutes: toNumber(s.minutes) })),
          },
        }),
      );
    }, t('plm.saved'));
  };
  const setLine = (key: number, patch: Partial<EditLine>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const setStep = (key: number, patch: Partial<EditStep>) => setSteps((ss) => ss.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  const moveStep = (i: number, by: number) =>
    setSteps((ss) => {
      const next = [...ss];
      const [s] = next.splice(i, 1);
      next.splice(i + by, 0, s!);
      return next;
    });
  return (
    <Dialog
      title={t('plm.edit_content_title', { name: c.name })}
      onClose={onClose}
      size="xl"
      footer={
        <span className="foot-actions">
          <span className="rp-quote-total">
            {t('plm.batch_estimate')}
            <strong className="tabular">{money(estimate.total)}</strong>
            <span className="muted small"> · {t('plm.per_unit_short', { amount: money(estimate.per_unit) })}</span>
          </span>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('plm.save_content')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <p className="muted small">{t('plm.editing_hint', { bom: c.bom_name, v: c.base_version })}</p>
        <div className="form-grid plm-batch-grid">
          <Field label={t('plm.f_output_qty')} required error={outErr} hint={bom ? t('plm.output_hint', { unit: bom.unit }) : undefined}>
            <input className="input" inputMode="decimal" value={output} onChange={(e) => setOutput(e.target.value)} />
          </Field>
          <Field label={t('plm.f_overhead')} error={ohErr} hint={t('plm.overhead_hint')}>
            <input className="input" inputMode="decimal" value={overhead} onChange={(e) => setOverhead(e.target.value)} />
          </Field>
        </div>
        <section>
          <h3 className="mt-h3">{t('plm.materials')}</h3>
          {linesErr ? (
            <p className="field-msg" role="alert">
              <Icon name="alert" size={14} /> {linesErr}
            </p>
          ) : null}
          <div className="plm-edit-rows" role="table" aria-label={t('plm.materials')}>
            {lines.length ? (
              <div className="plm-edit-head plm-edit-line muted small" role="row">
                <span role="columnheader">{t('plm.c_material')}</span>
                <span role="columnheader">{t('plm.c_qty')}</span>
                <span role="columnheader">{t('plm.c_scrap')}</span>
                <span role="columnheader">
                  <span className="sr-only">{t('plm.remove')}</span>
                </span>
              </div>
            ) : null}
            {lines.map((l) => {
              const it = items.get(l.item);
              const was = p.lines.find((x) => x.item === l.item);
              return (
                <div key={l.key} className={`plm-edit-line${was ? '' : ' plm-new-row'}`} role="row">
                  <span role="cell" className="plm-edit-name">
                    {it?.name ?? '—'}
                    {!was ? <span className="mt-chip">{t('plm.new_chip')}</span> : null}
                  </span>
                  <span role="cell" className="plm-with-unit">
                    <input
                      className="input"
                      inputMode="decimal"
                      value={l.qty}
                      aria-label={t('plm.qty_of', { name: it?.name ?? '' })}
                      aria-invalid={lineErr(l) || undefined}
                      onChange={(e) => setLine(l.key, { qty: e.target.value })}
                    />
                    <span className="muted small">{it?.unit}</span>
                  </span>
                  <span role="cell" className="plm-with-unit">
                    <input
                      className="input"
                      inputMode="decimal"
                      value={l.scrap}
                      placeholder="0"
                      aria-label={t('plm.scrap_of', { name: it?.name ?? '' })}
                      aria-invalid={scrapErr(l) || undefined}
                      onChange={(e) => setLine(l.key, { scrap: e.target.value })}
                    />
                    <span className="muted small">%</span>
                  </span>
                  <span role="cell">
                    <IconButton icon="trash" label={t('plm.remove_named', { name: it?.name ?? '' })} onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} />
                  </span>
                </div>
              );
            })}
          </div>
          <div className="rp-add">
            <select className="input" value={add} aria-label={t('plm.add_material')} onChange={(e) => setAdd(e.target.value)}>
              <option value="">{t('plm.choose_material')}</option>
              {free.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name} ({i.unit})
                </option>
              ))}
            </select>
            <Button
              size="sm"
              icon="plus"
              disabled={!add}
              onClick={() => {
                setLines((ls) => [...ls, { key: nextKey++, item: add, qty: '1', scrap: '' }]);
                setAdd('');
              }}
            >
              {t('plm.add_material')}
            </Button>
          </div>
        </section>
        <section>
          <h3 className="mt-h3">{t('plm.steps')}</h3>
          <div className="plm-edit-rows" role="table" aria-label={t('plm.steps')}>
            {steps.length ? (
              <div className="plm-edit-head plm-edit-step muted small" role="row">
                <span role="columnheader">{t('plm.c_step')}</span>
                <span role="columnheader">{t('plm.c_centre')}</span>
                <span role="columnheader">{t('plm.c_minutes')}</span>
                <span role="columnheader">
                  <span className="sr-only">{t('plm.order')}</span>
                </span>
              </div>
            ) : (
              <p className="muted small">{t('plm.no_steps')}</p>
            )}
            {steps.map((s, i) => (
              <div key={s.key} className="plm-edit-step" role="row">
                <span role="cell">
                  <input
                    className="input"
                    value={s.name}
                    maxLength={80}
                    aria-label={t('plm.step_name', { n: i + 1 })}
                    aria-invalid={(tried && !s.name.trim()) || undefined}
                    onChange={(e) => setStep(s.key, { name: e.target.value })}
                  />
                </span>
                <span role="cell">
                  <select className="input" value={s.workcenter} aria-label={t('plm.step_centre', { n: i + 1 })} onChange={(e) => setStep(s.key, { workcenter: e.target.value })}>
                    <option value="">{t('plm.no_centre')}</option>
                    {setup.workcenters.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </select>
                </span>
                <span role="cell" className="plm-with-unit">
                  <input
                    className="input"
                    inputMode="decimal"
                    value={s.minutes}
                    aria-label={t('plm.step_minutes', { n: i + 1 })}
                    aria-invalid={stepErr(s) || undefined}
                    onChange={(e) => setStep(s.key, { minutes: e.target.value })}
                  />
                  <span className="muted small">{t('plm.min')}</span>
                </span>
                <span role="cell" className="plm-step-tools">
                  <IconButton icon="up" label={t('plm.move_up', { n: i + 1 })} disabled={i === 0} onClick={() => moveStep(i, -1)} />
                  <IconButton icon="down" label={t('plm.move_down', { n: i + 1 })} disabled={i === steps.length - 1} onClick={() => moveStep(i, 1)} />
                  <IconButton icon="trash" label={t('plm.remove_step', { n: i + 1 })} onClick={() => setSteps((ss) => ss.filter((x) => x.key !== s.key))} />
                </span>
              </div>
            ))}
          </div>
          <div>
            <Button size="sm" icon="plus" onClick={() => setSteps((ss) => [...ss, { key: nextKey++, name: '', workcenter: '', minutes: '10' }])}>
              {t('plm.add_step')}
            </Button>
          </div>
        </section>
        <Field label={t('plm.f_note')} hint={t('plm.note_hint')}>
          <textarea className="input" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- versions of a bill of materials -----------------------------------------------------------

function HistoryDialog({
  id,
  canPropose,
  onClose,
  onOpen,
  onPropose,
}: {
  id: string;
  canPropose: boolean;
  onClose: () => void;
  onOpen: (id: string) => void;
  onPropose: (restore: number | null) => void;
}) {
  const { data: h } = useAction<History>('plm.history', { bom: id });
  const { data: changes } = useAction<{ changes: Row[] }>('plm.list', { view: 'open', bom: id });
  const [show, setShow] = useState<number | null>(null);
  if (!h)
    return (
      <Dialog title={t('plm.versions')} onClose={onClose} size="lg">
        <Skeleton h={360} />
      </Dialog>
    );
  const latest = h.versions[0]?.version ?? 1;
  return (
    <Dialog
      title={h.bom.name}
      onClose={onClose}
      size="lg"
      footer={
        canPropose && h.bom.active ? (
          <span className="foot-actions">
            <Button variant="primary" icon="plus" onClick={() => onPropose(null)}>
              {t('plm.propose_here')}
            </Button>
          </span>
        ) : undefined
      }
    >
      <div className="mt-detail">
        <p className="muted">
          {h.bom.item_name} · {h.versions.length ? t('plm.on_version', { v: latest }) : t('plm.no_versions_yet')}
        </p>
        {h.drift ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>{t('plm.drift')}</span>
          </p>
        ) : null}
        {changes?.changes.length ? (
          <section>
            <h3 className="mt-h3">{t('plm.open_changes')}</h3>
            <ul className="plm-open-list">
              {changes.changes.map((c) => (
                <li key={c.id}>
                  <button type="button" className="link" onClick={() => onOpen(c.id)}>
                    {c.name} · {c.title}
                  </button>
                  <StageBadge c={c} />
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        <section>
          <h3 className="mt-h3">{t('plm.versions')}</h3>
          {!h.versions.length ? (
            <p className="muted small">{t('plm.no_versions_body')}</p>
          ) : (
            <ol className="plm-versions">
              {h.versions.map((v) => (
                <li key={v.version} className={v.version === latest ? 'current' : undefined}>
                  <div className="plm-version-head">
                    <span className="plm-ver plm-ver-big">v{v.version}</span>
                    <span className="plm-version-what">
                      {v.change ? (
                        <button type="button" className="link" onClick={() => onOpen(v.change!.id)}>
                          {v.change.name} · {v.change.title}
                        </button>
                      ) : (
                        <strong>{versionNote(v.note) ?? t('plm.first_version')}</strong>
                      )}
                      <span className="muted small">
                        {[dateTime(v.applied_at), v.applied_by_name].filter(Boolean).join(' · ')} · {t('plm.per_unit_short', { amount: money(v.cost.per_unit) })}
                      </span>
                    </span>
                    {v.version === latest ? <StatusBadge tone="ok" icon="check" label={t('plm.in_use')} /> : null}
                    <span className="plm-version-tools">
                      <Button size="sm" variant="ghost" icon={show === v.version ? 'up' : 'eye'} aria-expanded={show === v.version} onClick={() => setShow(show === v.version ? null : v.version)}>
                        {show === v.version ? t('plm.hide') : t('plm.see_recipe')}
                      </Button>
                      {canPropose && h.bom.active && v.version !== latest ? (
                        <Button size="sm" icon="undo" onClick={() => onPropose(v.version)}>
                          {t('plm.bring_back')}
                        </Button>
                      ) : null}
                    </span>
                  </div>
                  {v.diff ? <DiffView diff={v.diff} names={h.names} empty={t('plm.same_as_before')} /> : null}
                  {show === v.version ? <ContentView c={v.content} names={h.names} /> : null}
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </Dialog>
  );
}
